import React from 'react';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';

export interface ShoppingSettings {
  merchantId: string;
  salesCountry: string;
  campaignPriority: number;
  enableLocal: boolean;
}

interface ShoppingSettingsStepProps {
  shoppingSettings: ShoppingSettings;
  onChangeShoppingSettings: (settings: ShoppingSettings) => void;
  errors?: {
    merchantId?: string;
    salesCountry?: string;
  };
}

export const ShoppingSettingsStep: React.FC<ShoppingSettingsStepProps> = ({
  shoppingSettings,
  onChangeShoppingSettings,
  errors = {},
}) => {
  const handleChange = (field: keyof ShoppingSettings, value: string | number | boolean) => {
    onChangeShoppingSettings({
      ...shoppingSettings,
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Shopping Campaign Settings</h3>
        <p className="text-sm text-gray-600">
          Configure your Google Merchant Center connection and campaign priority.
        </p>
      </div>

      <div className="space-y-6">
        {/* Merchant Center ID */}
        <div>
          <label htmlFor="merchant-id" className="block text-sm font-medium text-gray-700 mb-1">
            Merchant Center ID <span className="text-red-500">*</span>
          </label>
          <Input
            id="merchant-id"
            type="text"
            value={shoppingSettings.merchantId}
            onChange={(e) => handleChange('merchantId', e.target.value)}
            placeholder="e.g., 123456789"
            error={errors.merchantId}
            aria-invalid={!!errors.merchantId}
            aria-describedby={errors.merchantId ? 'merchant-id-error' : 'merchant-id-description'}
          />
          {errors.merchantId && (
            <p id="merchant-id-error" className="mt-1 text-sm text-red-600">
              {errors.merchantId}
            </p>
          )}
          {!errors.merchantId && (
            <p id="merchant-id-description" className="mt-1 text-xs text-gray-500">
              Your Google Merchant Center account ID. You can find this in your Merchant Center
              settings.
            </p>
          )}
        </div>

        {/* Sales Country */}
        <div>
          <label htmlFor="sales-country" className="block text-sm font-medium text-gray-700 mb-1">
            Sales Country <span className="text-red-500">*</span>
          </label>
          <Input
            id="sales-country"
            type="text"
            value={shoppingSettings.salesCountry}
            onChange={(e) => handleChange('salesCountry', e.target.value.toUpperCase())}
            placeholder="e.g., US"
            maxLength={2}
            error={errors.salesCountry}
            aria-invalid={!!errors.salesCountry}
            aria-describedby={
              errors.salesCountry ? 'sales-country-error' : 'sales-country-description'
            }
          />
          {errors.salesCountry && (
            <p id="sales-country-error" className="mt-1 text-sm text-red-600">
              {errors.salesCountry}
            </p>
          )}
          {!errors.salesCountry && (
            <p id="sales-country-description" className="mt-1 text-xs text-gray-500">
              Two-letter country code (e.g., US, GB, CA) where your products are sold. Must match
              your Merchant Center feed.
            </p>
          )}
        </div>

        {/* Campaign Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Campaign Priority
          </label>
          <div className="space-y-2">
            <div className="flex items-start">
              <input
                type="radio"
                id="priority-low"
                name="campaign-priority"
                value={0}
                checked={shoppingSettings.campaignPriority === 0}
                onChange={() => handleChange('campaignPriority', 0)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="priority-low" className="ml-2 flex-1 cursor-pointer">
                <span className="block text-sm font-medium text-gray-900">Low</span>
                <p className="text-xs text-gray-500">
                  Default priority for most Shopping campaigns
                </p>
              </label>
            </div>
            <div className="flex items-start">
              <input
                type="radio"
                id="priority-medium"
                name="campaign-priority"
                value={1}
                checked={shoppingSettings.campaignPriority === 1}
                onChange={() => handleChange('campaignPriority', 1)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="priority-medium" className="ml-2 flex-1 cursor-pointer">
                <span className="block text-sm font-medium text-gray-900">Medium</span>
                <p className="text-xs text-gray-500">
                  Higher priority than Low - useful when you have multiple Shopping campaigns
                </p>
              </label>
            </div>
            <div className="flex items-start">
              <input
                type="radio"
                id="priority-high"
                name="campaign-priority"
                value={2}
                checked={shoppingSettings.campaignPriority === 2}
                onChange={() => handleChange('campaignPriority', 2)}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <label htmlFor="priority-high" className="ml-2 flex-1 cursor-pointer">
                <span className="block text-sm font-medium text-gray-900">High</span>
                <p className="text-xs text-gray-500">
                  Highest priority - takes precedence over Low and Medium priority campaigns
                </p>
              </label>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            When multiple Shopping campaigns are eligible to show for the same product, priority
            determines which campaign is used.
          </p>
        </div>

        {/* Enable Local Inventory Ads */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <label className="flex items-start cursor-pointer">
            <Checkbox
              checked={shoppingSettings.enableLocal}
              onChange={(checked) => handleChange('enableLocal', checked)}
              aria-describedby="enable-local-description"
            />
            <div className="ml-3 flex-1">
              <span className="block text-sm font-medium text-gray-900">
                Enable Local Inventory Ads
              </span>
              <p id="enable-local-description" className="text-xs text-gray-500 mt-1">
                Show ads with information about your local store and inventory. Requires local
                product data in your Merchant Center account.
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Info Message */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex">
          <svg
            className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800">Shopping Campaign Requirements</h4>
            <p className="mt-1 text-xs text-blue-700">
              Before creating a Shopping campaign, ensure you have:
            </p>
            <ul className="mt-2 text-xs text-blue-700 list-disc list-inside space-y-1">
              <li>An approved Google Merchant Center account</li>
              <li>Product feed uploaded and approved in Merchant Center</li>
              <li>Your Merchant Center account linked to your Google Ads account</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
